// src/components/JsonToXmlConverter.js

import React, { useState, useRef, useEffect } from "react";
import {
  FaUpload,
  FaCopy,
  FaFileExport,
  FaArrowUp,
  FaArrowDown,
  FaMoon,
  FaSun,
} from "react-icons/fa";
import xmlFormatter from "xml-formatter";

const JsonToXmlConverter = () => {
  // Basic states
  const [jsonInput, setJsonInput] = useState("");
  const [xmlOutput, setXmlOutput] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Line numbers
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [lineNumbers, setLineNumbers] = useState([]);

  // Advanced panel
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rootElement, setRootElement] = useState("root");
  const [flattenArrays, setFlattenArrays] = useState(false);
  const [ignoreNull, setIgnoreNull] = useState(false);

  // Dark mode
  const [darkMode, setDarkMode] = useState(false);

  // Refs
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const outputRef = useRef(null);

  // Example JSON
  const exampleJson = `{
  "company": "SFDC247",
  "employees": [
    {
      "id": 1,
      "name": "John Doe",
      "role": "Salesforce Developer"
    },
    {
      "id": 2,
      "name": "Jane Smith",
      "role": "Salesforce Architect"
    }
  ]
}`;

  // Update line numbers whenever input changes or toggles
  useEffect(() => {
    if (showLineNumbers) {
      setLineNumbers(jsonInput.split("\n").map((_, i) => i + 1));
    }
  }, [jsonInput, showLineNumbers]);

  // Dark Mode
  useEffect(() => {
    if (darkMode) {
      document.body.style.backgroundColor = "#2d2d2d";
      document.body.style.color = "#f0f0f0";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }
  }, [darkMode]);

  // JSON -> XML Helper
  const jsonToXmlHelper = (value, tagName) => {
    // If ignoring null, skip them
    if (value === null && ignoreNull) {
      return "";
    }
    // strings, numbers, booleans
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return `<${tagName}>${value}</${tagName}>`;
    }
    // arrays
    if (Array.isArray(value)) {
      if (flattenArrays) {
        const singular = tagName.endsWith("s")
          ? tagName.slice(0, -1)
          : "item";
        return `<${tagName}>` +
          value
            .map((item) => `<${singular}>${jsonToXmlHelper(item, singular)}</${singular}>`)
            .join("") +
          `</${tagName}>`;
      } else {
        return value.map((item) => jsonToXmlHelper(item, tagName)).join("");
      }
    }
    // objects
    if (typeof value === "object" && value !== null) {
      return Object.keys(value)
        .map((key) => jsonToXmlHelper(value[key], key))
        .join("");
    }
    // fallback
    return `<${tagName}/>`;
  };

  // Convert JSON to XML with root element
  const convertJsonToXml = (parsedJson) => {
    const rawXml = `<${rootElement}>${jsonToXmlHelper(parsedJson, rootElement)}</${rootElement}>`;
    return xmlFormatter(rawXml, {
      indentation: "  ",
      collapseContent: true,
    });
  };

  // Convert button
  const handleConvert = () => {
    if (!jsonInput.trim()) {
      setError("No JSON input provided.");
      setXmlOutput("");
      return;
    }
    setProcessing(true);
    setTimeout(() => {
      try {
        const parsed = JSON.parse(jsonInput);
        const xml = convertJsonToXml(parsed);
        setXmlOutput(xml);
        setError("");
        setCopySuccess(false);
      } catch (err) {
        setError(`Invalid JSON: ${err.message}`);
        setXmlOutput("");
      }
      setProcessing(false);
    }, 600);
  };

  // Clear
  const clearAll = () => {
    setJsonInput("");
    setXmlOutput("");
    setError("");
    setCopySuccess(false);
  };

  // Copy
  const handleCopy = () => {
    navigator.clipboard.writeText(xmlOutput);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Export
  const handleExport = () => {
    const blob = new Blob([xmlOutput], { type: "application/xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "output.xml";
    link.click();
  };

  // Validate XML
  const handleValidateXml = () => {
    if (!xmlOutput.trim()) {
      alert("No XML to validate!");
      return;
    }
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlOutput, "application/xml");
      const errorNode = doc.querySelector("parsererror");
      if (errorNode) {
        alert("Invalid XML:\n" + errorNode.textContent);
      } else {
        alert("XML is well-formed!");
      }
    } catch (err) {
      alert("XML parse error: " + err.message);
    }
  };

  // Preview XML
  const handlePreviewXml = () => {
    if (!xmlOutput.trim()) {
      alert("No XML to preview!");
      return;
    }
    const newWin = window.open("", "_blank", "width=600,height=600");
    newWin.document.write(
      `<pre style="white-space:pre; font-family:monospace;">${xmlOutput
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</pre>`
    );
    newWin.document.close();
  };

  // Upload JSON
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJsonInput(ev.target.result);
      setError("");
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  // Scroll sync line numbers
  const syncScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  // Scroll output
  const scrollOutputTop = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop = 0;
    }
  };
  const scrollOutputBottom = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  };

  // Render Textarea with line numbers
  const renderTextAreaWithLineNumbers = () => {
    const lines = jsonInput.split("\n");
    return (
      <div style={{ position: "relative", display: "flex", height: "300px" }}>
        {showLineNumbers && (
          <div
            ref={lineNumbersRef}
            style={{
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              borderRight: "1px solid #ccc",
              width: "50px",
              textAlign: "right",
              userSelect: "none",
              color: "#6c757d",
              paddingTop: "5px",
            }}
          >
            {lines.map((_, i) => (
              <div key={i} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                {i + 1}
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={jsonInput}
          onScroll={syncScroll}
          onChange={(e) => {
            setJsonInput(e.target.value);
            if (showLineNumbers) {
              setLineNumbers(e.target.value.split("\n").map((_, idx) => idx + 1));
            }
          }}
          className="form-control"
          placeholder="Enter your JSON here..."
          style={{
            flex: 1,
            resize: "none",
            borderRadius: 0,
            border: error ? "2px solid red" : "2px solid #ccc",
            overflowY: "auto",
            fontFamily: "monospace",
            paddingLeft: showLineNumbers ? "0.5em" : "1em",
          }}
        />
        {/* Upload icon */}
        <label
          htmlFor="jsonUpload"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
          }}
        >
          <FaUpload size={20} />
        </label>
        <input
          id="jsonUpload"
          type="file"
          style={{ display: "none" }}
          accept=".json"
          onChange={handleFileUpload}
        />
      </div>
    );
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "900px" }}>
      {/* Dark Mode Toggle */}
      <div style={{ textAlign: "right", marginBottom: "10px" }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? <FaSun /> : <FaMoon />} {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      {/* Possibly show error/spinner */}
      <div className="text-center mb-3">
        {processing && <div className="alert alert-info">Processing...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        <button
          className="btn btn-info"
          onClick={() => {
            setJsonInput(exampleJson);
            setError("");
          }}
        >
          Use Example Data
        </button>
      </div>

      {/* JSON Text Area */}
      <div className="row mb-3">
        <div className="col-md-12">{renderTextAreaWithLineNumbers()}</div>
      </div>

      {/* Convert / Clear / LN / Show Advanced */}
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={handleConvert} disabled={processing}>
          {processing ? "Converting..." : "Convert"}
        </button>
        <button className="btn btn-secondary me-2" onClick={clearAll}>
          Clear
        </button>
        <label className="me-3">
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
          />{" "}
          Show Line Numbers
        </label>
        <button
          className="btn btn-outline-primary"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? "Hide Advanced" : "Show Advanced"}
        </button>
      </div>

      {/* Advanced Panel */}
      {showAdvanced && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "10px",
            backgroundColor: darkMode ? "#444" : "#f8f9fa",
          }}
        >
          <label className="me-3">
            Root Element:
            <input
              type="text"
              value={rootElement}
              onChange={(e) => setRootElement(e.target.value)}
              style={{ marginLeft: "5px" }}
            />
          </label>
          <label className="me-3">
            <input
              type="checkbox"
              checked={flattenArrays}
              onChange={(e) => setFlattenArrays(e.target.checked)}
            />{" "}
            Flatten Arrays
          </label>
          <label className="me-3">
            <input
              type="checkbox"
              checked={ignoreNull}
              onChange={(e) => setIgnoreNull(e.target.checked)}
            />{" "}
            Ignore Null Fields
          </label>
        </div>
      )}

      {/* Output */}
      {xmlOutput && (
        <div className="mt-4">
          <h5>Converted XML</h5>
          <div
            ref={outputRef}
            style={{
              height: "300px",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              border: "2px solid #ccc",
              borderRadius: "5px",
            }}
          >
            <pre
              style={{
                margin: 0,
                padding: "10px",
                whiteSpace: "pre",
                fontFamily: "monospace",
              }}
            >
              {xmlOutput}
            </pre>
          </div>
          {/* Bottom row: single horizontal layout for all buttons */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button className="btn btn-secondary" onClick={scrollOutputTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary" onClick={scrollOutputBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"}`}
              onClick={handleCopy}
            >
              <FaCopy /> Copy Output
            </button>
            <button className="btn btn-warning" onClick={handleExport}>
              <FaFileExport /> Export XML
            </button>
            <button className="btn btn-info" onClick={handlePreviewXml}>
              Preview in New Tab
            </button>
            <button className="btn btn-info" onClick={handleValidateXml}>
              Validate XML
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JsonToXmlConverter;